import React, { useState } from 'react';
import FlatList from '../../../Base/Components/FlatList';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import PermanentMedicationItem, {
	IPermanentMedicament,
} from './PernamentMedicationItem';
import usePermanentMedication from '../../Hooks/usePermanentMedication';
import useFlatData from '../../../Base/Hooks/useFlatData';
import { useAppContext } from '../../../../Services/AppContext';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
// @ts-ignore
import styles from './PermanentMedication.module.scss';
import Tooltip from '../../../../Components/Popup/Tooltip';
import Text from '../../../../Components/Base/Text/Text';
import ElectronicPrescriptionModal from '../../../Prescription/Components/ElectronicPrescriptionModal';
import PermanentMedicationCreateModal from './PernamentMedicationCreate/PermanentMedicationCreateModal';
import useTranslation from '../../../../Hooks/useTranslation';
import usePatientMeta from '../../../../Components/PatientProfile/Hooks/usePatientMeta';
import { saveToUserMeta } from '../../../User/Services/userMeta.service';
import { MetaKey } from '../../../Meta/Enums/MetaKey.enum';
import { prepareDefaultDataFromPermanentMedication } from '../../Services/permanentMedication.service';
import PatientMedicalHistoryAddDataModal from '../../../Patient/Components/PatientMedicalHistory/PatientMedicalHistoryAddDataModal';

interface Props {
	patientId: string;
}

const wrapperId = 'permanentMedicationWrapper';

const PermanentMedication: React.FC<Props> = ({ patientId }) => {
	const { workplaceId } = useAppContext();
	const { canCreateCaseOnPermanentMedication, reFetchMeta } =
		usePatientMeta(patientId);
	const { t } = useTranslation();
	const [selectedItems, setSelectedItems] = useState<IPermanentMedicament[]>(
		[],
	);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [isAddOpen, setIsAddOpen] = useState(false);
	const [isAddToMedicalHistoryOpen, setIsAddToMedicalHistoryOpen] =
		useState(false);
	const [dataFormMedicalHistory, setDataFormMedicalHistory] = useState<
		Record<string, string> | undefined
	>(undefined);
	const [defaultValues, setDefaultValues] = useState<object | undefined>(
		undefined,
	);

	const issueAgain = (item: IPermanentMedicament) => {
		const defaults = prepareDefaultDataFromPermanentMedication([item]);
		setDefaultValues(defaults);
		setIsCreateOpen(true);
	};

	const issueSelectedAgain = () => {
		const defaults = prepareDefaultDataFromPermanentMedication(selectedItems);
		setDefaultValues(defaults);
		setIsCreateOpen(true);
	};

	const setToMedicalHistory = () => {
		const value = flatData
			.map(entry => {
				const item = entry.data?.item ?? {};
				const manual = entry.data?.manual ?? '';
				const name = item.name || '';
				const strength = item.strength || '';
				const form = item.form || '';
				const packaging = item.packaging || '';

				return `${name} ${strength} ${form} ${packaging} (${manual})`.trim();
			})
			.join(', ');
		setDataFormMedicalHistory({ medicationPermanent: value });
		setIsAddToMedicalHistoryOpen(true);
	};

	const handleCreateNewPermanentMedication = () => {
		setIsAddOpen(false);
		loadData();
	};

	const handleOnSelectMedicament = (medicament: IPermanentMedicament) => {
		const alreadyInSelectedItem = selectedItems.find(
			item => item.id === medicament.id,
		);
		if (alreadyInSelectedItem) {
			setSelectedItems(selectedItems.filter(item => item.id !== medicament.id));
		} else {
			setSelectedItems(prev => [...prev, medicament]);
		}
	};

	const handleCanCreateCaseOnPermanentMedication = async (value: boolean) => {
		await saveToUserMeta({
			userId: patientId,
			key: MetaKey.CAN_CREATE_CASE_ON_PERMANENT_MEDICATION,
			value: value,
			workplaceId: workplaceId,
		});
		await reFetchMeta();
	};

	const {
		getPermanentMedication,
		getNextPage,
		data,
		isLoading,
		page,
		setPage,
	} = usePermanentMedication();

	const { flatData, hasMoreData, loadData, setFilter } = useFlatData(
		getPermanentMedication,
		{
			data: data?.userMedicationLasted?.data,
			totalPages: data?.userMedicationLasted?.meta?.totalPages,
			page: data?.userMedicationLasted?.meta?.page,
		},
		page,
		setPage,
		{ userId: patientId, workplaceId },
	);

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.options}>
					<div className={styles.leftPart}>
						{/*{false && (*/}
						{/*	<LinkButton*/}
						{/*		title='component.permanentMedication.addSelectedToPharmacyAnamnesis'*/}
						{/*		theme={Theme.BLUE}*/}
						{/*	/>*/}
						{/*)}*/}
						<LinkButton
							title='component.permanentMedication.addMedicament'
							theme={Theme.BLUE}
							onClick={() => setIsAddOpen(true)}
						/>
						<LinkButton
							title='component.permanentMedication.issueSelected'
							theme={Theme.BLUE}
							onClick={issueSelectedAgain}
						/>
						<LinkButton
							title='component.permanentMedication.setToMedicalHistory'
							theme={Theme.BLUE}
							onClick={setToMedicalHistory}
						/>
					</div>
					<div className={styles.rightPart}>
						{canCreateCaseOnPermanentMedication ? (
							<>
								<div>
									<img
										className={styles.icon}
										src='/dist/icons/v2/check-green.svg'
										alt='green check'
									/>{' '}
									<Text
										className={styles.text}
										tx='component.permanentMedication.patientCanAskForMedicaments'
										inline
									/>
								</div>
								<Tooltip
									trigger={
										<LinkButton
											title='component.permanentMedication.ban'
											theme={Theme.RED}
											onClick={() =>
												handleCanCreateCaseOnPermanentMedication(false)
											}
										/>
									}>
									{t('component.permanentMedication.toolTipBan')}
								</Tooltip>
							</>
						) : (
							<>
								<div>
									<img
										className={styles.icon}
										src='/dist/icons/xmark-solid-red.svg'
										alt='green check'
									/>{' '}
									<Text
										className={styles.text}
										tx='component.permanentMedication.patientCantAskForMedicaments'
										inline
									/>
								</div>
								<Tooltip
									trigger={
										<LinkButton
											title='component.permanentMedication.allow'
											theme={Theme.BLUE}
											onClick={() =>
												handleCanCreateCaseOnPermanentMedication(true)
											}
										/>
									}>
									{t('component.permanentMedication.toolTipAllow')}
								</Tooltip>
							</>
						)}
					</div>
				</div>
				<ScrollContainer wrapperId={wrapperId}>
					<FlatList
						onRefresh={loadData}
						onLoadMore={getNextPage}
						data={flatData}
						isLoading={isLoading}
						hasMoreData={hasMoreData}
						scrollableTargetId={wrapperId}
						renderItem={item => (
							<PermanentMedicationItem
								item={item}
								onEdit={loadData}
								onDelete={loadData}
								onSelectMedicament={handleOnSelectMedicament}
								isSelected={selectedItems.some(
									medicament => medicament.id === item.id,
								)}
								onIssueAgain={issueAgain}
							/>
						)}
					/>
				</ScrollContainer>
			</div>
			{isCreateOpen && (
				<ElectronicPrescriptionModal
					opened
					onClose={() => setIsCreateOpen(false)}
					patientId={patientId}
					defaultValues={defaultValues}
				/>
			)}
			{isAddOpen && (
				<PermanentMedicationCreateModal
					isOpen
					patientId={patientId}
					onClose={() => setIsAddOpen(false)}
					onCreate={handleCreateNewPermanentMedication}
				/>
			)}
			{isAddToMedicalHistoryOpen && dataFormMedicalHistory && (
				<PatientMedicalHistoryAddDataModal
					isOpen
					patientId={patientId}
					data={dataFormMedicalHistory}
					onClose={() => setIsAddToMedicalHistoryOpen(false)}
				/>
			)}
		</>
	);
};

export default PermanentMedication;
